export const SSO_ERROR_PAGE_UID = 'sso-login-error'
export const SSO_LOGIN_FAILED_KEY = 'sso_login_failed'
export const SSO_HANDLED_KEY = 'sso_handled_code'
export const SSO_ERROR_DEBUG_KEY = 'sso_login_error_debug'
export const SSO_ERROR_DEFAULT = 'default'

export const SSO_BUSINESS_ERROR_CODES = {
  NOT_PROFESSIONAL_ACCOUNT: '41000083',
} as const

export type SsoBusinessErrorCode =
  typeof SSO_BUSINESS_ERROR_CODES[keyof typeof SSO_BUSINESS_ERROR_CODES]

const KNOWN_SSO_ERROR_CODES = new Set<string>(
  Object.values(SSO_BUSINESS_ERROR_CODES)
)

export const isKnownSsoErrorCode = (
  code: string
): code is SsoBusinessErrorCode => KNOWN_SSO_ERROR_CODES.has(code)

export const buildSsoErrorPageUrl = (reason: string | null): string => {
  const basePath = `/pages/${SSO_ERROR_PAGE_UID}`
  if (!reason || !isKnownSsoErrorCode(reason)) {
    return basePath
  }
  return `${basePath}?reason=${encodeURIComponent(reason)}`
}

export const isSsoErrorPageRoute = (
  pathname: string,
  uid?: string | string[]
): boolean =>
  pathname === '/pages/[uid]' &&
  (uid === SSO_ERROR_PAGE_UID ||
    (Array.isArray(uid) && uid[0] === SSO_ERROR_PAGE_UID))

export const extractAtlanticLoginErrorCode = (action: {
  type?: string
  err?: { response?: { data?: { code?: string | number } } }
}): string | null => {
  if (action?.type !== 'ATLANTIC_LOGIN_FAILURE') {
    return null
  }

  const backendCode = action?.err?.response?.data?.code
  if (backendCode == null) {
    return null
  }

  const normalizedCode = String(backendCode)
  if (isKnownSsoErrorCode(normalizedCode)) {
    return normalizedCode
  }

  return null
}

/** Debug helper: logs the Atlantic login API error response in the browser console. */
export const logAtlanticLoginFailure = (action: {
  type?: string
  err?: {
    response?: { status?: number; data?: unknown }
    message?: string
  }
}): void => {
  const responseData = action?.err?.response?.data
  const extractedCode = extractAtlanticLoginErrorCode(action)
  const debugPayload = {
    actionType: action?.type,
    httpStatus: action?.err?.response?.status,
    responseBody: responseData,
    extractedCode,
    redirectUrl: buildSsoErrorPageUrl(extractedCode),
  }

  if (typeof window !== 'undefined') {
    sessionStorage.setItem(SSO_ERROR_DEBUG_KEY, JSON.stringify(debugPayload))
  }

  console.group('[SSO] Atlantic login failure')
  console.log('Action type:', debugPayload.actionType)
  console.log('HTTP status:', debugPayload.httpStatus)
  console.log('Response body:', debugPayload.responseBody)
  console.log('Extracted business code:', debugPayload.extractedCode)
  console.log('Redirect URL:', debugPayload.redirectUrl)
  console.groupEnd()
}

export const readAtlanticLoginFailureDebug = (): Record<
  string,
  unknown
> | null => {
  if (typeof window === 'undefined') return null
  const raw = sessionStorage.getItem(SSO_ERROR_DEBUG_KEY)
  if (!raw) return null
  try {
    return JSON.parse(raw) as Record<string, unknown>
  } catch {
    return null
  }
}

export const isOnSsoPage = (url: string): boolean => {
  if (url.includes('/pages/sso-login-error')) return true
  if (url.includes('/pages/redirect-direct-access')) return true

  const logoutUrl = process.env.SSO_LOGOUT_REDIRECT_URL
  const errorUrl = process.env.SSO_ERROR_REDIRECT_URL
  if (logoutUrl && url.includes(logoutUrl)) return true
  if (errorUrl && url.includes(errorUrl)) return true
  return false
}

export const clearSsoErrorSession = (): void => {
  sessionStorage.removeItem(SSO_LOGIN_FAILED_KEY)
  sessionStorage.removeItem(SSO_HANDLED_KEY)
}

/** Clears only the handled OAuth code so the user can retry login from the error page. */
export const clearSsoHandledOAuthCode = (): void => {
  sessionStorage.removeItem(SSO_HANDLED_KEY)
}
